import {ReactNode, useState} from "react";
import NumberInputLabel from "../../elements/numberInputLabel.element";
import DefaultedNumberInputLabel from "../../elements/defaultedNumberInputLabel.element";
import StringInputLabel from "../../elements/stringInputLabel.element";
import {StartingUnitCommand} from "../../commands/startingUnit.command";
import {StartingBuildingCommand} from "../../commands/startingBuilding.command";
import {useIntegrator} from "../../integrator.context";
import {ChangeConstantsCommand} from "../../commands/changeConstants.command";
import {CONSTANT_DEFAULTS, DefaultedConstant, NoiseMap} from "../../models/remoteConstants.model";
import CombatSimulationView from "./combatSimulation.view";
import SelectableLabel from "../../elements/selectable.label";
import Collapsible from "../../elements/collapsible";
import {formulaStatus, SCORE_KIND_LABEL, ScoreKind, scoreVariablesFor} from "../../utils/scoreFormula";

// One collapsible group of General settings.
//
// Driven in Collapsible's CONTROLLED mode purely so the whole header toggles, not just
// the caret: Collapsible self-toggles only on clicks that land on the header background
// (`e.target === e.currentTarget`), which a styled <p> title would swallow. The caret
// stops propagation and toggles on its own, so there is no double-toggle.
//
// Open by default, so the page reads exactly as it did before — the sections are there to
// let a designer fold away what they aren't tuning, not to hide anything by default.
function Section(props: { title: string, children: ReactNode }) {
    const [open, setOpen] = useState(true);

    return (
        <Collapsible open={open} onOpenChange={setOpen}
                     title={<p className={'text header'} style={{cursor: 'pointer'}}
                               onClick={() => setOpen(!open)}>{props.title}</p>}>
            <div className={'layout vertical gap'} style={{marginTop: 10}}>
                {props.children}
            </div>
        </Collapsible>
    );
}

// One per-kind Score Formula (what ONE unit / building / city / tech is worth): the input
// and, when the game would reject the formula, the warning that says so. The Score fields
// themselves are never written from here — each shows its in-game value beside it instead.
function ScoreFormulaRow(props: { kind: ScoreKind, field: string, value: string, onChange(value: string): void }) {
    const status = formulaStatus(props.value, scoreVariablesFor(props.kind));
    const variables = scoreVariablesFor(props.kind).join(', ');
    return (
        <div className={'layout vertical'} style={{gap: 4}}>
            <StringInputLabel field={props.field} value={props.value} placeholder={'SCORE'} onChange={props.onChange}/>
            {status === 'invalid' &&
                <p style={{color: '#ff6666', fontSize: 12}}>
                    <span className={'fa-solid fa-triangle-exclamation'} style={{marginRight: 6}}/>
                    {`Invalid formula: every ${SCORE_KIND_LABEL[props.kind].toLowerCase()} is worth 0 in game until it is fixed. Use ${variables}, numbers, + - * / and parentheses.`}
                </p>}
        </div>
    );
}

// A General number with a Client-side default (CONSTANT_DEFAULTS): shown and marked as the
// default while the branch leaves it out, and removed again by the reset.
function DefaultedConstantInput(props: { name: DefaultedConstant, step?: string, displayDecimals?: number }) {
    const {state, executeCommand} = useIntegrator();
    const name = props.name;
    return (
        <DefaultedNumberInputLabel field={`general.${name}`} value={state[name]} defaultValue={CONSTANT_DEFAULTS[name]}
                                   step={props.step} displayDecimals={props.displayDecimals}
                                   onChange={v => executeCommand(new ChangeConstantsCommand(d => d[name] = v))}
                                   onReset={() => executeCommand(new ChangeConstantsCommand(d => delete d[name]))}/>
    );
}

function GeneralEditorView() {
    const {state, executeCommand} = useIntegrator();

    //set default values so they'll be added to the remoteconstantsmodel
    state.elevationNoise ??= {} as NoiseMap;
    state.temperatureNoise ??= {} as NoiseMap;

    return (
        // minHeight: 0 is what makes the two columns' own scrolling work. Without
        // it this row's height comes from its content, so neither the settings
        // column's overflowY below nor the Simulations list's scroller inside
        // CombatSimulation ever engages — the whole tab scrolls in the pane
        // wrapper instead, taking Combat Simulation with it. (`flex: 1` sets the
        // basis on the horizontal main axis here, so it does NOT bound height.)
        <div className={'layout horizontal box flex-grow stretch'} style={{gap: 10, minHeight: 0}}>
            <div className={'layout vertical gap'} style={{flex: 1, minWidth: 300, gap: 20, overflowY: 'auto'}}>
                <Section title={'Starting'}>
                    <SelectableLabel field={'general.startingUnit'} value={state.startingUnit} options={state.units.map((b) => b.id)}
                                onChange={value => executeCommand(new StartingUnitCommand(value))}/>
                    <SelectableLabel field={'general.startingBuilding'} value={state.startingBuilding}
                                options={state.buildings.map((b) => b.id)}
                                onChange={value => executeCommand(new StartingBuildingCommand(value))}/>
                </Section>

                <Section title={'Unit Capacity'}>
                    <DefaultedConstantInput name={'capitalCityUnitCapacity'}/>
                    <DefaultedConstantInput name={'additionalCityUnitCapacity'}/>
                </Section>

                <Section title={'Gold'}>
                    <NumberInputLabel field={'general.startingGold'} value={state.startingGold}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.startingGold = v))}/>
                    <NumberInputLabel field={'general.capitalCityGoldPerTurn'} value={state.capitalCityGoldPerTurn}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.capitalCityGoldPerTurn = v))}/>
                    <NumberInputLabel field={'general.cityGoldPerTurn'} value={state.cityGoldPerTurn}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.cityGoldPerTurn = v))}/>
                </Section>

                <Section title={'Misc'}>
                    <DefaultedConstantInput name={'turnsToFog'}/>
                    {/* Connection Bonus: 0 by default, meaning "off" until authored. */}
                    <DefaultedConstantInput name={'connectionBonusCityPopulation'}/>
                    <DefaultedConstantInput name={'connectionBonusCapitalPopulation'}/>
                </Section>

                <Section title={'Combat'}>
                    <StringInputLabel field={'general.attackDamageFormula'} value={state.attackDamageFormula}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.attackDamageFormula = v))}/>
                    <DefaultedConstantInput name={'minimumDamage'}/>
                    <StringInputLabel field={'general.retaliatoryDamageFormula'} value={state.retaliatoryDamageFormula}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.retaliatoryDamageFormula = v))}/>
                    <DefaultedConstantInput name={'minimumRetaliatoryDamage'}/>
                    <DefaultedConstantInput name={'criticalHealthPercent'}/>
                    <DefaultedConstantInput name={'criticalAttackPenalty'}/>
                    <DefaultedConstantInput name={'criticalDefensePenalty'}/>
                </Section>

                <Section title={'Score'}>
                    <StringInputLabel field={'general.playerScoreFormula'} value={state.playerScoreFormula}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.playerScoreFormula = v))}/>
                    <ScoreFormulaRow kind={'unit'} field={'general.unitScoreFormula'} value={state.unitScoreFormula ?? ''}
                                     onChange={v => executeCommand(new ChangeConstantsCommand(d => d.unitScoreFormula = v))}/>
                    <ScoreFormulaRow kind={'building'} field={'general.buildingScoreFormula'} value={state.buildingScoreFormula ?? ''}
                                     onChange={v => executeCommand(new ChangeConstantsCommand(d => d.buildingScoreFormula = v))}/>
                    <ScoreFormulaRow kind={'city'} field={'general.cityScoreFormula'} value={state.cityScoreFormula ?? ''}
                                     onChange={v => executeCommand(new ChangeConstantsCommand(d => d.cityScoreFormula = v))}/>
                    <ScoreFormulaRow kind={'tech'} field={'general.techScoreFormula'} value={state.techScoreFormula ?? ''}
                                     onChange={v => executeCommand(new ChangeConstantsCommand(d => d.techScoreFormula = v))}/>
                    <p className={'muted-text'} style={{fontSize: 12}}>
                        Each of the four formulas is what ONE unit, building, city (Starting Slots &gt; 0) or tech is worth,
                        and is the source of truth for its kind. SCORE = the entity's Player Score field; COST = its gold price
                        (units, buildings, techs); TURNS = a tier's research turns (techs); LEVEL = a city's current level (cities,
                        which are founded rather than bought). Blank = SCORE, i.e. the Player Score fields alone.
                        The fields are never rewritten: each shows its "Score by Formula" underneath.
                    </p>
                    <DefaultedConstantInput name={'goldScore'}/>
                    <DefaultedConstantInput name={'goldPerTurnScore'}/>
                    <DefaultedConstantInput name={'tileInTerritoryScore'}/>
                    <DefaultedConstantInput name={'tileFullVisionScore'}/>
                    <DefaultedConstantInput name={'tileFogVisionScore'}/>
                </Section>

                <Section title={'Resume Playback'}>
                    <DefaultedConstantInput name={'resumePlaybackInterval'} step={'0.05'} displayDecimals={2}/>
                    <DefaultedConstantInput name={'resumePlaybackOrdersPerPlayer'}/>
                    <DefaultedConstantInput name={'resumePlaybackMaxOrders'}/>
                    <p className={'muted-text'} style={{fontSize: 12}}>
                        When a multiplayer or bot game is resumed, what the other players did since your own last order is
                        played out again rather than shown already done: each player's last orders you can see, up to Orders
                        Per Player each and Max Orders in all, the latest first. Research and orders under cloud or fog are not
                        counted and pass at once, so with nothing in sight the game starts right away. The interval is the pause
                        before the first order and after each has played out. Selecting, orders, End Turn and Resign wait for it
                        to end. 0 for either count turns it off.
                    </p>
                </Section>
                <Section title={'Elevation Noise Map'}>
                    <NumberInputLabel step={'0.01'} displayDecimals={3} field={'general.elevationNoise.scale'} value={state.elevationNoise.scale}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.elevationNoise.scale = v))}/>
                    <NumberInputLabel field={'general.elevationNoise.octaves'} value={state.elevationNoise.octaves}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.elevationNoise.octaves = v))}/>
                    <NumberInputLabel step={'0.01'} displayDecimals={3} field={'general.elevationNoise.lacunarity'} value={state.elevationNoise.lacunarity}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.elevationNoise.lacunarity = v))}/>
                    <NumberInputLabel step={'0.01'} displayDecimals={3} field={'general.elevationNoise.persistance'} value={state.elevationNoise.persistance}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.elevationNoise.persistance = v))}/>
                </Section>
                <Section title={'Temperature Noise Map'}>
                    <NumberInputLabel step={'0.01'} displayDecimals={3} field={'general.temperatureNoise.scale'} value={state.temperatureNoise.scale}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.temperatureNoise.scale = v))}/>
                    <NumberInputLabel field={'general.temperatureNoise.octaves'} value={state.temperatureNoise.octaves}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.temperatureNoise.octaves = v))}/>
                    <NumberInputLabel step={'0.01'} displayDecimals={3} field={'general.temperatureNoise.lacunarity'} value={state.temperatureNoise.lacunarity}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.temperatureNoise.lacunarity = v))}/>
                    <NumberInputLabel step={'0.01'} displayDecimals={3} field={'general.temperatureNoise.persistance'} value={state.temperatureNoise.persistance}
                                      onChange={v => executeCommand(new ChangeConstantsCommand(d => d.temperatureNoise.persistance = v))}/>
                </Section>
            </div>
            <CombatSimulationView/>
        </div>
    )
}

export default GeneralEditorView;